<template>
  <v-container fluid class="admin-contracts">
    <NcrmHomePageHeader
      icon="mdi-file-sign"
      :title="$t('admin.contracts.title')"
      :subtitle="$t('admin.contracts.subtitle')"
    />

    <v-alert v-if="errorMessage" type="error" class="mt-4">{{ errorMessage }}</v-alert>
    <v-alert v-if="deletedMessage" type="success" class="mt-4">{{ deletedMessage }}</v-alert>
    <NcrmHomeKpiCards class="mt-4" :items="kpiItems" />

    <v-card variant="flat" class="admin-contracts__panel mt-4">
      <div class="admin-contracts__filters">
        <v-text-field
          v-model="search"
          :label="$t('admin.contracts.filters.search')"
          prepend-inner-icon="mdi-magnify"
          density="compact"
          clearable
          hide-details
          @update:model-value="scheduleLoad"
        />
        <v-select
          v-model="statusFilter"
          :items="statusFilterItems"
          item-title="title"
          item-value="value"
          :label="$t('admin.contracts.filters.status')"
          density="compact"
          hide-details
          @update:model-value="loadCompanies"
        />
      </div>

      <v-data-table-server
        :headers="headers"
        :items="companies"
        :items-length="total"
        :loading="loading"
        :items-per-page="limit"
        :page="page"
        item-value="company_id"
        density="comfortable"
        :row-props="() => ({ style: 'cursor: pointer' })"
        @click:row="openCompany"
        @update:options="handleOptions"
      >
        <template #item.company="{ item }">
          <div class="admin-contracts__company">
            <strong>{{ item.company_name || $t("admin.contracts.table.unnamedCompany") }}</strong>
            <span>{{ $t("admin.contracts.table.companyId", { id: item.company_id }) }}</span>
          </div>
        </template>
        <template #item.contract="{ item }">
          <v-chip :color="statusColor(item.platform_contract_status)">
            {{ $t(`admin.contracts.statuses.${item.platform_contract_status}`) }}
          </v-chip>
        </template>
        <template #item.entitlements="{ item }">
          {{ $t("admin.contracts.table.entitlementCount", { count: item.entitlement_keys?.length || 0 }) }}
        </template>
        <template #item.trial="{ item }">{{ formatDate(item.trial_ends_at) }}</template>
        <template #item.row_nav><v-icon class="admin-contracts__chevron">mdi-chevron-right</v-icon></template>
        <template #no-data>
          <v-alert type="info" class="ma-4">{{ $t("admin.contracts.empty") }}</v-alert>
        </template>
      </v-data-table-server>
    </v-card>

    <AdminPlatformContractDrawer
      v-model="drawerOpen"
      :company="selectedCompany"
      :loading="detailLoading"
      @saved="handleSaved"
      @deleted="handleDeleted"
    />
  </v-container>
</template>

<script>
import NcrmHomeKpiCards from "@/components/ncrm/shared/NcrmHomeKpiCards.vue";
import NcrmHomePageHeader from "@/components/ncrm/shared/NcrmHomePageHeader.vue";
import AdminPlatformContractDrawer from "./contracts/AdminPlatformContractDrawer.vue";
import {
  formatAdminContractDate,
  getAdminPlatformContract,
  listAdminPlatformContracts,
} from "./contracts/adminContractsApi.mjs";

export default {
  name: "AdminPlatformContractsHome",
  components: { AdminPlatformContractDrawer, NcrmHomeKpiCards, NcrmHomePageHeader },
  data() {
    return {
      companies: [], summary: { total: 0, signed: 0, unsigned: 0, terminated: 0 }, total: 0,
      page: 1, limit: 25, search: "", statusFilter: "", loading: false, errorMessage: "",
      drawerOpen: false, detailLoading: false, selectedCompany: null, detailRequestId: 0, searchTimer: null,
      deletedMessage: "", deletedTimer: null,
    };
  },
  computed: {
    kpiItems() {
      return [
        { key: "total", label: this.$t("admin.contracts.metrics.total"), value: this.summary.total, icon: "mdi-domain", color: "primary" },
        { key: "signed", label: this.$t("admin.contracts.metrics.signed"), value: this.summary.signed, icon: "mdi-file-check-outline", color: "success" },
        { key: "unsigned", label: this.$t("admin.contracts.metrics.unsigned"), value: this.summary.unsigned, icon: "mdi-file-clock-outline", color: "warning" },
        { key: "terminated", label: this.$t("admin.contracts.metrics.terminated"), value: this.summary.terminated, icon: "mdi-file-cancel-outline", color: "error" },
      ];
    },
    headers() {
      return [
        { title: this.$t("admin.contracts.table.company"), key: "company", sortable: false },
        { title: this.$t("admin.contracts.table.contract"), key: "contract", sortable: false },
        { title: this.$t("admin.contracts.table.number"), key: "platform_contract_number", sortable: false },
        { title: this.$t("admin.contracts.table.entitlements"), key: "entitlements", sortable: false },
        { title: this.$t("admin.contracts.table.license"), key: "license_package_key", sortable: false },
        { title: this.$t("admin.contracts.table.trial"), key: "trial", sortable: false },
        { title: "", key: "row_nav", sortable: false, width: 48 },
      ];
    },
    statusFilterItems() {
      return [
        { value: "", title: this.$t("admin.contracts.filters.allStatuses") },
        ...["unsigned", "signed", "terminated"].map((value) => ({ value, title: this.$t(`admin.contracts.statuses.${value}`) })),
      ];
    },
  },
  mounted() { this.loadCompanies(); },
  beforeUnmount() { clearTimeout(this.searchTimer); clearTimeout(this.deletedTimer); },
  methods: {
    formatDate: formatAdminContractDate,
    statusColor(status) { return { signed: "success", terminated: "error", unsigned: "warning" }[status] || "secondary"; },
    scheduleLoad() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => { this.page = 1; this.loadCompanies(); }, 300);
    },
    handleOptions(options = {}) {
      const nextPage = Number(options.page) || 1;
      const nextLimit = Number(options.itemsPerPage) || 25;
      if (nextPage === this.page && nextLimit === this.limit && this.companies.length) return;
      this.page = nextPage; this.limit = nextLimit; this.loadCompanies();
    },
    async loadCompanies() {
      this.loading = true; this.errorMessage = "";
      try {
        const { data } = await listAdminPlatformContracts({ page: this.page, limit: this.limit, search: this.search || undefined, status: this.statusFilter || undefined });
        this.companies = Array.isArray(data?.companies) ? data.companies : [];
        this.total = Number(data?.pagination?.total || 0);
        this.summary = { ...this.summary, ...(data?.summary || {}) };
      } catch (error) {
        this.errorMessage = error?.response?.data?.message || this.$t("admin.contracts.errors.load");
      } finally { this.loading = false; }
    },
    async openCompany(_event, payload) {
      const row = payload?.item?.raw ?? payload?.item ?? payload;
      if (!row?.company_id) return;
      const requestId = this.detailRequestId + 1;
      this.detailRequestId = requestId;
      this.drawerOpen = true; this.detailLoading = true; this.selectedCompany = row;
      try {
        const { data } = await getAdminPlatformContract(row.company_id);
        if (requestId !== this.detailRequestId) return;
        this.selectedCompany = data;
      } catch (error) {
        if (requestId !== this.detailRequestId) return;
        this.errorMessage = error?.response?.data?.message || this.$t("admin.contracts.errors.detail");
        this.drawerOpen = false;
      } finally { if (requestId === this.detailRequestId) this.detailLoading = false; }
    },
    handleSaved(updatedCompany) {
      if (updatedCompany?.company_id) {
        this.selectedCompany = updatedCompany;
        this.companies = this.companies.map((company) => (
          company.company_id === updatedCompany.company_id
            ? { ...company, ...updatedCompany }
            : company
        ));
      }
      this.loadCompanies();
    },
    handleDeleted(result) {
      this.selectedCompany = null;
      this.deletedMessage = this.$t("admin.contracts.manualDelete.deleted", {
        company: result?.company_name || "",
      });
      clearTimeout(this.deletedTimer);
      this.deletedTimer = setTimeout(() => { this.deletedMessage = ""; }, 6000);
      this.loadCompanies();
    },
  },
};
</script>

<style scoped>
.admin-contracts { min-height: 100%; background: var(--app-page-background); }
.admin-contracts__panel { overflow: hidden; border: 1px solid rgba(var(--v-theme-on-surface), 0.08); border-radius: var(--app-radius-base); }
.admin-contracts__filters { display: grid; grid-template-columns: minmax(240px, 1fr) minmax(200px, 280px); gap: 12px; padding: 20px; }
.admin-contracts__company { display: grid; gap: 2px; }
.admin-contracts__company strong { color: var(--app-text-strong); }
.admin-contracts__company span { color: var(--app-text-default); font-size: 0.75rem; opacity: 0.52; }
.admin-contracts__chevron { opacity: 0.42; }
@media (max-width: 720px) { .admin-contracts__filters { grid-template-columns: 1fr; } }
</style>
